import React, { useEffect, useState } from 'react';
import { Button, Card, Form, List, message, Modal, Table } from 'antd';
import { useParams } from 'react-router-dom';
import formService from '../../../services/formService';
import LocalStorageService from '../../../services/localStorageService';
import EditFormItem from '../form/EditFormItem';
import InputFormItem from '../form/InputFormItem';

const Enrolls = (props) => {
  const [form] = Form.useForm();
  const { fetchEnrollCourseList, enrollList } = props;
  const studentId = useParams().id;
  const [isEnrollEdit, setIsEnrollEdit] = useState(false);
  const [courseModalVisible, setCourseModalVisible] = useState(false);
  const [courseTypeModalVisible, setCourseTypeModalVisible] = useState(false);
  const [teacherModalVisible, setTeacherModalVisible] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [courseTypeList, setCourseTypeList] = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEnrollCourseList(studentId);
  }, []);

  const latestEnroll = (enrollList || []).reduce((latest, item) => {
    if (!latest) return item;
    const latestTime = latest.updatedAtRaw
      ? new Date(latest.updatedAtRaw).getTime()
      : 0;
    const itemTime = item.updatedAtRaw
      ? new Date(item.updatedAtRaw).getTime()
      : 0;
    return itemTime > latestTime ? item : latest;
  }, null);

  useEffect(() => {
    form.setFieldsValue({
      createBy: latestEnroll ? latestEnroll.createBy : '',
      updateDate: latestEnroll ? latestEnroll.updatedAt : '',
    });
  }, [form, latestEnroll]);

  const openCourseModal = async (record) => {
    setSelectedEnrollment(record);
    setCourseModalVisible(true);
    setLoading(true);
    try {
      const res = await formService.queryCourse(false);
      const courses = (res.data || []).sort((a, b) => {
        const aWeight =
          typeof a.weight === 'number' ? a.weight : Number(a.code || 0);
        const bWeight =
          typeof b.weight === 'number' ? b.weight : Number(b.code || 0);
        return aWeight - bWeight;
      });
      setCourseList(courses);
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message;
      if (errorMessage) {
        message.error(`更改失败: ${errorMessage}`);
      }
      message.error('课程名列表读取失败');
    } finally {
      setLoading(false);
    }
  };

  const openCourseTypeModal = async (record) => {
    setSelectedEnrollment(record);
    setCourseTypeModalVisible(true);
    setLoading(true);
    try {
      const res = await formService.queryCourseCategory();
      const courseTypes = (res.data || [])
        .filter((item) => item.isActive !== false)
        .sort((a, b) => Number(a.weight || 0) - Number(b.weight || 0));
      setCourseTypeList(courseTypes);
    } catch (error) {
      message.error('课程类型列表读取失败');
    } finally {
      setLoading(false);
    }
  };

  const openTeacherModal = async (record) => {
    setSelectedEnrollment(record);
    setTeacherModalVisible(true);
    setLoading(true);
    try {
      const res = await formService.queryTeacher();
      setTeacherList(
        (res.data || []).filter((teacher) => teacher.isActive !== false)
      );
    } catch (error) {
      message.error('老师列表读取失败');
    } finally {
      setLoading(false);
    }
  };

  const updateEnrollment = async (data) => {
    if (!selectedEnrollment) return;
    setLoading(true);
    try {
      await formService.updateEnrollment(selectedEnrollment.key, {
        ...data,
        createBy: LocalStorageService.getCurrentUser(),
      });
      message.success('更改成功');
      setCourseModalVisible(false);
      setCourseTypeModalVisible(false);
      setTeacherModalVisible(false);
      fetchEnrollCourseList(studentId);
    } catch (error) {
      message.error('更改失败');
    } finally {
      setLoading(false);
    }
  };

  const toggleReEnroll = async (record) => {
    setLoading(true);
    try {
      await formService.updateEnrollment(record.key, {
        reEnroll: Number(record.reEnroll || 0) !== 1,
        createBy: LocalStorageService.getCurrentUser(),
      });
      message.success('更改成功');
      fetchEnrollCourseList(studentId);
    } catch (error) {
      message.error('更改失败');
    } finally {
      setLoading(false);
    }
  };

  const tableColumns = [
    {
      title: '是否为续课',
      dataIndex: 'reEnroll',
      key: 'enroll_reEnroll',
      render: (text, record) => (
        <span>
          <span>{Number(text || 0)}</span>
          <Button
            size='small'
            style={{ marginLeft: 8 }}
            disabled={!isEnrollEdit}
            onClick={() => toggleReEnroll(record)}
          >
            更改
          </Button>
        </span>
      ),
    },
    {
      title: '课程类型',
      dataIndex: 'courseTypeName',
      key: 'enroll_courseTypeName',
      render: (text, record) => (
        <span>
          <span>{text || '-'}</span>
          <Button
            size='small'
            style={{ marginLeft: 8 }}
            disabled={!isEnrollEdit}
            onClick={() => openCourseTypeModal(record)}
          >
            更改
          </Button>
        </span>
      ),
    },
    {
      title: '课程名',
      dataIndex: 'courseName',
      key: 'enroll_courseName',
      render: (text, record) => (
        <span>
          <span>{text || '-'}</span>
          <Button
            size='small'
            style={{ marginLeft: 8 }}
            disabled={!isEnrollEdit}
            onClick={() => openCourseModal(record)}
          >
            更改
          </Button>
        </span>
      ),
    },
    {
      title: '老师名字',
      dataIndex: 'teacherName',
      key: 'enroll_teacherName',
      render: (text, record) => (
        <span>
          <span>{text}</span>
          <Button
            size='small'
            style={{ marginLeft: 8 }}
            disabled={!isEnrollEdit}
            onClick={() => openTeacherModal(record)}
          >
            更改
          </Button>
        </span>
      ),
    },
    {
      title: '推荐人',
      dataIndex: 'advisorName',
      key: 'enroll_advisorName',
    },
    {
      title: '报课日期',
      dataIndex: 'enrollDate',
      key: 'enroll_enrollDate',
    },
    {
      title: '报课价格',
      dataIndex: 'total_price',
      key: 'enroll_total_price',
    },
    {
      title: '未付金额',
      dataIndex: 'balance',
      key: 'enroll_balance',
    },
  ];

  return (
    <Card>
      <Table dataSource={enrollList} columns={tableColumns} />
      <Form layout='horizontal' form={form} style={{ marginTop: 20 }}>
        <InputFormItem label='修改人' fieldName='createBy' isDisabled={true} />
        <InputFormItem
          label='修改时间'
          fieldName='updateDate'
          isDisabled={true}
        />
        <EditFormItem
          toggleBasicEdit={setIsEnrollEdit}
          isBasicEdit={isEnrollEdit}
          handleSubmit={() => setIsEnrollEdit(false)}
        />
      </Form>
      <Modal
        title='更改课程名'
        visible={courseModalVisible}
        footer={null}
        onCancel={() => setCourseModalVisible(false)}
      >
        <List
          loading={loading}
          dataSource={courseList}
          renderItem={(course) => (
            <List.Item
              actions={[
                <Button
                  type='link'
                  onClick={() => updateEnrollment({ course: course._id })}
                >
                  更改
                </Button>,
              ]}
            >
              {course.name}
            </List.Item>
          )}
        />
      </Modal>
      <Modal
        title='更改课程类型'
        visible={courseTypeModalVisible}
        footer={null}
        onCancel={() => setCourseTypeModalVisible(false)}
      >
        <List
          loading={loading}
          dataSource={courseTypeList}
          renderItem={(courseType) => (
            <List.Item
              actions={[
                <Button
                  type='link'
                  onClick={() =>
                    updateEnrollment({ courseType: courseType.name })
                  }
                >
                  更改
                </Button>,
              ]}
            >
              {courseType.name}
            </List.Item>
          )}
        />
      </Modal>
      <Modal
        title='更改老师'
        visible={teacherModalVisible}
        footer={null}
        onCancel={() => setTeacherModalVisible(false)}
      >
        <List
          loading={loading}
          dataSource={teacherList}
          renderItem={(teacher) => (
            <List.Item
              actions={[
                <Button
                  type='link'
                  onClick={() => updateEnrollment({ teacher: teacher._id })}
                >
                  更改
                </Button>,
              ]}
            >
              {teacher.cnname}
            </List.Item>
          )}
        />
      </Modal>
    </Card>
  );
};

export default Enrolls;
